import { message } from 'antd';
import axios from 'axios';

function requireEnv(name: string, value: string | undefined): string {
  if (!value) {
    throw new Error(`Missing required environment variable: ${name}`);
  }
  return value;
}

// API base URL — must be provided via VITE_API_URL at build/run time.
export const API_URL = requireEnv('VITE_API_URL', import.meta.env.VITE_API_URL);

/**
 * A frontend PWA publikus URL-je. Az admin innen nyit PWA login / magic-link
 * linkeket. Konfigurálás: VITE_FRONTEND_URL env. Fallback
 * heurisztika: ha az admin Traefik domainen fut (admin.foo.bar), levágjuk az
 * 'admin.' prefixet. Egyébként localhost:3002.
 */
export const FRONTEND_URL: string =
  import.meta.env.VITE_FRONTEND_URL ||
  (typeof window !== 'undefined' && window.location.hostname.startsWith('admin.')
    ? `${window.location.protocol}//${window.location.hostname.replace(/^admin\./, '')}`
    : 'http://localhost:3002');

/**
 * Útvonalak, amelyek hibájára az interceptor NEM dob toastot: az oldal saját,
 * lefordított Alertje az egyetlen visszajelzés. A kiszolgáló üzenete itt
 * angol és szándékosan semmitmondó (#109) — toastként duplán és idegen
 * nyelven szólna.
 */
const SILENT_ERROR_PATHS = [
  '/auth/forgot-password',
  '/auth/reset-password',
  '/auth/reset-password/validate',
];

export const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor for auth
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Don't retry for auth endpoints or if already retried
    if (
      error.response?.status === 401 &&
      !originalRequest._retry &&
      !originalRequest.url?.includes('/auth/')
    ) {
      originalRequest._retry = true;

      try {
        const refreshToken = localStorage.getItem('refresh_token');
        if (refreshToken) {
          // Use axios directly to avoid interceptor loop
          const response = await axios.post(`${API_URL}/auth/refresh`, {
            refresh_token: refreshToken,
          });

          const { access_token, refresh_token: newRefreshToken, user } = response.data;
          localStorage.setItem('access_token', access_token);
          if (newRefreshToken) {
            localStorage.setItem('refresh_token', newRefreshToken);
          }

          originalRequest.headers.Authorization = `Bearer ${access_token}`;
          return api(originalRequest);
        } else {
          // No refresh token available
          throw new Error('No refresh token available');
        }
      } catch (refreshError) {
        // Refresh failed, clear tokens but don't redirect here
        // Let the auth store handle redirects
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');

        // Only redirect if we're not already on the login page.
        // Megőrizzük az eredeti path-ot ?next=... query paramban, így a
        // Login.tsx után visszanavigálhatunk oda, ahol épp voltunk.
        if (!window.location.pathname.includes('/login')) {
          const current = window.location.pathname + window.location.search;
          const next = current === '/' ? '' : `?next=${encodeURIComponent(current)}`;
          window.location.href = `/login${next}`;
        }
        return Promise.reject(refreshError);
      }
    }

    // A caller can opt out of this automatic toast by setting `silentError` on
    // the request config, and then owns the messaging itself.
    //
    // It exists because the toast is per REQUEST, and a screen that refreshes
    // itself in the background makes many. Measured on the raffle control panel
    // with the backend stopped for half a minute: its poll produced a stack of
    // "Network Error" toasts that covered the control buttons — during a live
    // draw, an outage the organizer can wait out became a panel they could not
    // use. Existing callers pass nothing and are unaffected.
    // The password-reset endpoints (#109) are silent by PATH instead: their
    // pages show a translated Alert of their own, so a toast would repeat the
    // server's deliberately vague English message. Both switches must stay —
    // `api.errorToast.test.ts` pins each one.
    const silentError =
      Boolean((originalRequest as { silentError?: boolean } | undefined)?.silentError) ||
      SILENT_ERROR_PATHS.includes(originalRequest?.url ?? '');

    if (
      !silentError &&
      (error.response?.status !== 401 || originalRequest.url?.includes('/auth/'))
    ) {
      if (error.response?.data?.message) {
        message.error(error.response.data.message);
      } else if (error.message && !error.message.includes('refresh token')) {
        message.error(error.message);
      }
    }

    return Promise.reject(error);
  }
);

export default api;
